import { entitySelectionPayload } from '../../../shared/utils/entitySelection';
import { hasCalendarEventField } from '../config/calendarEventTypes';
import { parseCalendarAmount } from '../utils/calendar.amount';
import { validateEventForm } from '@/modules/calendar/utils';
import { getStoredSessionToken } from '../../../app/services/session.service';

const CALENDAR_API_BASE_URL = (process.env.REACT_APP_CALENDAR_API_BASE_URL || '').replace(
  /\/$/,
  ''
);
const CALENDAR_API_PATH = '/api/calendar/events';
const TENANT_DB_ERROR_CODES = new Set([
  'tenant_database_not_ready',
  'tenant_database_unavailable',
  'tenant_database_metadata_missing',
]);

function buildMutationResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function getCalendarApiUrl(path = '') {
  const resolvedPath = path || CALENDAR_API_PATH;
  return `${CALENDAR_API_BASE_URL}${resolvedPath}`;
}

function isAuthFailure(status) {
  return status === 401 || status === 403;
}

async function isModuleDisabledResponse(response) {
  if (response.status !== 403) return false;

  try {
    const payload = await response.clone().json();
    return payload?.error === 'module_disabled';
  } catch {
    return false;
  }
}

async function readErrorPayload(response) {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

function mapTenantDatabaseError(payload) {
  if (!TENANT_DB_ERROR_CODES.has(payload?.error)) {
    return null;
  }

  return {
    error:
      payload.error === 'tenant_database_not_ready'
        ? 'CALENDAR_TENANT_DATABASE_NOT_READY'
        : payload.error === 'tenant_database_metadata_missing'
          ? 'CALENDAR_TENANT_DATABASE_METADATA_MISSING'
          : 'CALENDAR_TENANT_DATABASE_UNAVAILABLE',
    message: payload?.message || 'Database tenant non disponibile per il modulo calendario.',
  };
}

function getCalendarAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function normalizeAttendees(attendees) {
  if (!Array.isArray(attendees)) {
    return [];
  }

  return attendees.filter((value) => (typeof value === 'string' || typeof value === 'number') && String(value).trim()).map((value) => String(value).trim());
}

function normalizeOptionalString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeOptionalId(value) {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  return value;
}

function buildLinkedEntityRoute(type, id) {
  if (!type || !id) return '';

  const encodedId = encodeURIComponent(String(id));

  switch (type) {
    case 'client':
    case 'cliente':
      return `/clienti?clientId=${encodedId}`;
    case 'supplier':
    case 'fornitore':
      return `/fornitori/${encodedId}`;
    case 'proposal':
      return `/proposte-affari?dealId=${encodedId}`;
    case 'in-progress-deal':
      return `/affari-in-corso?dealId=${encodedId}`;
    case 'completed-deal':
      return `/affari-completati?dealId=${encodedId}`;
    case 'deal':
      return `/affari-in-corso?dealId=${encodedId}`;
    case 'assignment':
    case 'task':
      return `/incarichi?assignmentId=${encodedId}`;
    case 'document':
      return `/documenti?documentId=${encodedId}`;
    case 'payment':
      return `/contabilita-aziendale?paymentId=${encodedId}`;
    case 'invoice':
      return `/contabilita-aziendale?invoiceId=${encodedId}`;
    case 'expense':
      return `/uscite?expenseId=${encodedId}`;
    default:
      return '';
  }
}

function getLinkedEntityModule(type) {
  switch (type) {
    case 'client':
    case 'cliente':
      return 'clients';
    case 'supplier':
    case 'fornitore':
      return 'suppliers';
    case 'proposal':
    case 'in-progress-deal':
    case 'completed-deal':
    case 'deal':
    case 'assignment':
    case 'task':
      return 'deals';
    case 'document':
      return 'documents';
    case 'payment':
    case 'invoice':
    case 'expense':
      return 'accounting';
    default:
      return '';
  }
}

function mapApiLinkedEntity(event) {
  const type = normalizeOptionalString(
      event?.linkedEntityType ||
      event?.sourceType ||
      (event?.clientId ? 'client' : '') ||
      (event?.supplierId ? 'supplier' : '') ||
      (event?.dealId ? 'deal' : '') ||
      (event?.assignmentId || event?.taskId ? 'assignment' : '') ||
      (event?.documentId ? 'document' : '') ||
      (event?.accountingPaymentId || event?.paymentId ? 'payment' : '') ||
      (event?.accountingExpenseId || event?.expenseId ? 'expense' : '')
  );
  const id = normalizeOptionalId(
    event?.linkedEntityId ||
      event?.sourceId ||
      event?.clientId ||
      event?.supplierId ||
      event?.dealId ||
      event?.assignmentId ||
      event?.taskId ||
      event?.documentId ||
      event?.accountingPaymentId ||
      event?.paymentId ||
      event?.accountingExpenseId ||
      event?.expenseId
  );

  if (!type || !id) {
    return null;
  }

  return {
    type,
    id,
    label:
      normalizeOptionalString(event?.linkedEntityLabel) ||
      normalizeOptionalString(event?.sourceLabel) ||
      '',
    moduleKey: normalizeOptionalString(event?.linkedEntityModule) || getLinkedEntityModule(type),
    route: normalizeOptionalString(event?.linkedEntityRoute) || buildLinkedEntityRoute(type, id),
  };
}

function buildEventDuration(input) {
  return input.duration === 'custom'
    ? normalizeOptionalString(input.durationCustom)
    : input.duration;
}

function buildStartsAt(date, time) {
  if (!date || !time) {
    return '';
  }

  return `${date}T${time}:00`;
}

function buildEndsAt(date, endDate, time) {
  if (!endDate || !time || endDate === date) {
    return null;
  }

  return `${endDate}T${time}:00`;
}

function normalizePayloadId(value) {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function mapCalendarEventToApiPayload(input) {
  const hasField = (field) => hasCalendarEventField(input.type, field);
  const payload = {
    title: input.title.trim(),
    description: normalizeOptionalString(input.description),
    startsAt: buildStartsAt(input.date, hasField('time') ? input.time : '00:00'),
    endsAt: hasCalendarEventField(input.type, 'endDate') ? buildEndsAt(input.date, input.endDate, input.time) : null,
    duration: hasField('duration') ? buildEventDuration(input) : '',
    type: input.type,
    attendeeIds: hasField('attendees') ? normalizeAttendees(input.attendees) : [],
    location: hasField('location') ? normalizeOptionalString(input.location) : '',
    priority: hasField('priority') ? input.priority : 'media',
    ...entitySelectionPayload(input),
    clientId: hasField('clientId') ? normalizePayloadId(input.clientId) : null,
    supplierId: hasField('supplierId') ? normalizePayloadId(input.supplierId) : null,
    ...(!hasField('clientId') ? { clientIds: [] } : {}),
    ...(!hasField('supplierId') ? { supplierIds: [] } : {}),
    ...(hasField('amount') ? { amount: parseCalendarAmount(input.amount) } : {}),
  };

  if (Object.prototype.hasOwnProperty.call(input, 'customFields')) {
    payload.customFields = input.customFields;
  }

  return payload;
}

function mapApiEventToCalendarEvent(event) {
  const startsAt = typeof event?.startsAt === 'string' ? event.startsAt : '';
  const linkedEntity = mapApiLinkedEntity(event);

  const mappedEvent = {
    id: event?.id,
    title: event?.title || '',
    description: event?.description || '',
    date: startsAt ? startsAt.slice(0, 10) : '',
    time: startsAt && hasCalendarEventField(event?.type, 'time') ? startsAt.slice(11, 16) : '',
    duration: event?.duration || '1h',
    type: event?.type || 'meeting',
    attendees: normalizeAttendees(event?.attendeeIds),
    location: event?.location || '',
    priority: event?.priority || 'media',
    ...entitySelectionPayload(event),
    clientId: event?.clientId ?? '',
    supplierId: event?.supplierId ?? '',
    ...(event?.amount != null ? { amount: event.amount } : {}),
    ...(event?.customFields ? { customFields: event.customFields } : {}),
  };

  if (event?.notes) mappedEvent.notes = event.notes;
  if (hasCalendarEventField(mappedEvent.type, 'endDate') && typeof event?.endsAt === 'string') {
    mappedEvent.endDate = event.endsAt.slice(0, 10);
    mappedEvent.endTime = event.endsAt.slice(11, 16);
  }
  if (event?.status) mappedEvent.status = event.status;
  if (event?.createdByName) mappedEvent.createdBy = event.createdByName;
  else if (event?.createdBy) mappedEvent.createdBy = event.createdBy;
  else if (event?.creatorName) mappedEvent.createdBy = event.creatorName;
  else if (event?.creator) mappedEvent.createdBy = event.creator;
  if (event?.sourceType) mappedEvent.sourceType = event.sourceType;
  if (event?.sourceId) mappedEvent.sourceId = event.sourceId;
  if (event?.sourceLabel) mappedEvent.sourceLabel = event.sourceLabel;
  if (linkedEntity) {
    mappedEvent.linkedEntity = linkedEntity;
    mappedEvent.linkedEntityType = linkedEntity.type;
    mappedEvent.linkedEntityId = linkedEntity.id;
  }

  return mappedEvent;
}

function mapApiEventNoteToComment(note) {
  return {
    id: note?.id,
    eventId: note?.eventId,
    parentId: note?.parentId ?? null,
    authorUserId: note?.authorUserId || '',
    authorName: note?.authorName || '',
    authorFullName: note?.authorFullName || '',
    body: note?.body || '',
    text: note?.body || '',
    createdAt: note?.createdAt || null,
  };
}

async function performListCalendarEventsRequest() {
  const response = await fetch(getCalendarApiUrl(CALENDAR_API_PATH), {
    headers: getCalendarAuthHeaders(),
  });
  const tenantDbPayload = await readErrorPayload(response);
  const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
  if (tenantDbError) {
    const error = new Error(tenantDbError.error);
    error.userMessage = tenantDbError.message;
    throw error;
  }

  if (await isModuleDisabledResponse(response)) {
    throw new Error('CALENDAR_MODULE_DISABLED');
  }

  if (!response.ok) {
    throw new Error(`CALENDAR_EVENTS_LIST_FAILED_${response.status}`);
  }

  const payload = await response.json();
  if (!Array.isArray(payload)) {
    throw new Error('CALENDAR_EVENTS_LIST_INVALID_PAYLOAD');
  }

  return payload.map(mapApiEventToCalendarEvent);
}

export async function listCalendarEvents() {
  try {
    return await performListCalendarEventsRequest();
  } catch {
    return [];
  }
}

export async function listCalendarEventsResult() {
  try {
    const events = await performListCalendarEventsRequest();

    return {
      ok: true,
      data: events,
      error: null,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'CALENDAR_API_UNAVAILABLE';
    const tenantDbError =
      errorMessage === 'CALENDAR_TENANT_DATABASE_NOT_READY' ||
      errorMessage === 'CALENDAR_TENANT_DATABASE_METADATA_MISSING' ||
      errorMessage === 'CALENDAR_TENANT_DATABASE_UNAVAILABLE';

    return {
      ok: false,
      data: [],
      error: tenantDbError
        ? errorMessage
        : error instanceof Error && error.message === 'CALENDAR_MODULE_DISABLED'
          ? 'CALENDAR_MODULE_DISABLED'
          : error instanceof Error && /CALENDAR_EVENTS_LIST_FAILED_(401|403)$/.test(error.message)
            ? 'CALENDAR_AUTH_REQUIRED'
            : error instanceof Error
              ? error.message
              : 'CALENDAR_API_UNAVAILABLE',
      message:
        tenantDbError && error instanceof Error && typeof error.userMessage === 'string'
          ? error.userMessage
          : error instanceof Error && error.message === 'CALENDAR_MODULE_DISABLED'
          ? 'Modulo calendario non abilitato per questo workspace.'
          : '',
    };
  }
}

export async function createCalendarEvent(input, idempotencyKey = '') {
  const errors = validateEventForm(input);

  if (Object.keys(errors).length > 0) {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_EVENT_VALIDATION_ERROR',
      data: { errors },
      message: 'Compila correttamente i campi obbligatori',
    });
  }

  try {
    const response = await fetch(getCalendarApiUrl(CALENDAR_API_PATH), {
      method: 'POST',
      headers: getCalendarAuthHeaders({
        'Content-Type': 'application/json',
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      }),
      body: JSON.stringify(mapCalendarEventToApiPayload(input)),
    });
    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CALENDAR_MODULE_DISABLED',
        message: 'Modulo calendario non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'CALENDAR_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend calendario.',
      });
    }

    if (!response.ok) {
      throw new Error(`CALENDAR_EVENT_CREATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        event: mapApiEventToCalendarEvent(payload),
      },
      message: 'Evento creato con successo!',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_API_UNAVAILABLE',
      message: 'Backend calendario non disponibile. Creazione non completata.',
    });
  }
}

export async function updateCalendarEvent(id, input) {
  if (!id) {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_EVENT_NOT_FOUND',
      message: 'Evento non trovato.',
    });
  }

  const errors = validateEventForm(input);

  if (Object.keys(errors).length > 0) {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_EVENT_VALIDATION_ERROR',
      data: { errors },
      message: 'Compila correttamente i campi obbligatori',
    });
  }

  try {
    const response = await fetch(getCalendarApiUrl(`${CALENDAR_API_PATH}/${id}`), {
      method: 'PUT',
      headers: getCalendarAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(mapCalendarEventToApiPayload(input)),
    });
    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'CALENDAR_EVENT_NOT_FOUND',
        message: 'Evento non trovato.',
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CALENDAR_MODULE_DISABLED',
        message: 'Modulo calendario non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'CALENDAR_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend calendario.',
      });
    }

    if (!response.ok) {
      throw new Error(`CALENDAR_EVENT_UPDATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        event: mapApiEventToCalendarEvent(payload),
      },
      message: 'Evento aggiornato con successo!',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_API_UNAVAILABLE',
      message: 'Backend calendario non disponibile. Aggiornamento non completato.',
    });
  }
}

export async function deleteCalendarEvent(id) {
  if (!id) {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_EVENT_NOT_FOUND',
      message: 'Evento non trovato.',
    });
  }

  try {
    const response = await fetch(getCalendarApiUrl(`${CALENDAR_API_PATH}/${id}`), {
      method: 'DELETE',
      headers: getCalendarAuthHeaders(),
    });
    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'CALENDAR_EVENT_NOT_FOUND',
        message: 'Evento non trovato.',
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CALENDAR_MODULE_DISABLED',
        message: 'Modulo calendario non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildMutationResult({
        ok: false,
        error: 'CALENDAR_AUTH_REQUIRED',
        message: 'Sessione non autorizzata per il backend calendario.',
      });
    }

    if (!response.ok) {
      throw new Error(`CALENDAR_EVENT_DELETE_FAILED_${response.status}`);
    }

    return buildMutationResult({
      ok: true,
      data: {
        deletedEventId: id,
      },
      message: 'Evento eliminato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_API_UNAVAILABLE',
      message: 'Backend calendario non disponibile. Eliminazione non completata.',
    });
  }
}

export async function listCalendarEventNotesResult(eventId) {
  if (!eventId || String(eventId).includes(':')) {
    return buildMutationResult({ ok: true, data: [], error: null });
  }

  try {
    const response = await fetch(getCalendarApiUrl(`${CALENDAR_API_PATH}/${eventId}/notes`), {
      headers: getCalendarAuthHeaders(),
    });
    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({ ok: false, error: tenantDbError.error, message: tenantDbError.message });
    }

    if (!response.ok) {
      return buildMutationResult({
        ok: false,
        error: response.status === 404 ? 'CALENDAR_EVENT_NOT_FOUND' : `CALENDAR_EVENT_NOTES_LIST_FAILED_${response.status}`,
        data: [],
        message: 'Note evento non disponibili.',
      });
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: Array.isArray(payload) ? payload.map(mapApiEventNoteToComment) : [],
      error: null,
    });
  } catch {
    return buildMutationResult({
      ok: false,
      data: [],
      error: 'CALENDAR_API_UNAVAILABLE',
      message: 'Backend calendario non disponibile. Note non caricate.',
    });
  }
}

export async function createCalendarEventNoteResult(eventId, payload) {
  const body = normalizeOptionalString(payload?.body || payload?.text || payload);
  if (!eventId || String(eventId).includes(':') || !body) {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_EVENT_NOTE_VALIDATION_ERROR',
      message: 'Scrivi una nota prima di salvarla.',
    });
  }

  try {
    const response = await fetch(getCalendarApiUrl(`${CALENDAR_API_PATH}/${eventId}/notes`), {
      method: 'POST',
      headers: getCalendarAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        body,
        ...(payload?.parentId !== null && payload?.parentId !== undefined ? { parentId: payload.parentId } : {}),
      }),
    });
    const tenantDbError = mapTenantDatabaseError(await readErrorPayload(response));
    if (tenantDbError) {
      return buildMutationResult({ ok: false, error: tenantDbError.error, message: tenantDbError.message });
    }

    if (!response.ok) {
      return buildMutationResult({
        ok: false,
        error: response.status === 404 ? 'CALENDAR_EVENT_NOT_FOUND' : `CALENDAR_EVENT_NOTE_CREATE_FAILED_${response.status}`,
        message: 'Nota evento non salvata.',
      });
    }

    const responsePayload = await response.json();
    return buildMutationResult({
      ok: true,
      data: { comment: mapApiEventNoteToComment(responsePayload) },
      error: null,
      message: 'Nota salvata.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_API_UNAVAILABLE',
      message: 'Backend calendario non disponibile. Nota non salvata.',
    });
  }
}

export async function deleteCalendarEventNoteResult(eventId, noteId) {
  if (!eventId || !noteId || String(eventId).includes(':')) {
    return buildMutationResult({ ok: false, error: 'CALENDAR_EVENT_NOTE_NOT_FOUND' });
  }

  try {
    const response = await fetch(getCalendarApiUrl(`${CALENDAR_API_PATH}/${eventId}/notes/${noteId}`), {
      method: 'DELETE',
      headers: getCalendarAuthHeaders(),
    });
    if (!response.ok) {
      return buildMutationResult({
        ok: false,
        error: response.status === 404 ? 'CALENDAR_EVENT_NOTE_NOT_FOUND' : `CALENDAR_EVENT_NOTE_DELETE_FAILED_${response.status}`,
        message: 'Nota evento non eliminata.',
      });
    }

    return buildMutationResult({
      ok: true,
      data: { deletedCommentId: noteId },
      message: 'Nota eliminata.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CALENDAR_API_UNAVAILABLE',
      message: 'Backend calendario non disponibile. Nota non eliminata.',
    });
  }
}

export {
  mapApiEventToCalendarEvent,
  mapCalendarEventToApiPayload,
  mapApiEventNoteToComment,
};

const CALENDAR_CUSTOM_FIELDS_PATH = '/api/calendar/custom-fields';

export async function listCalendarCustomFields() {
  const response = await fetch(getCalendarApiUrl(CALENDAR_CUSTOM_FIELDS_PATH), { headers: getCalendarAuthHeaders() });
  const payload = await readErrorPayload(response);
  if (!response.ok) throw new Error(payload?.message || 'Impossibile caricare i campi personalizzati.');
  return payload;
}

export async function createCalendarCustomField(definition) {
  const response = await fetch(getCalendarApiUrl(CALENDAR_CUSTOM_FIELDS_PATH), { method: 'POST', headers: getCalendarAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(definition) });
  const payload = await readErrorPayload(response);
  if (!response.ok) return buildMutationResult({ ok: false, error: 'CUSTOM_FIELD_CREATE_FAILED', message: payload?.message || 'Impossibile salvare il campo personalizzato.' });
  return buildMutationResult({ ok: true, data: payload });
}

export async function archiveCalendarCustomField(id) {
  const response = await fetch(getCalendarApiUrl(`${CALENDAR_CUSTOM_FIELDS_PATH}/${id}`), { method: 'DELETE', headers: getCalendarAuthHeaders() });
  const payload = await readErrorPayload(response);
  if (!response.ok) return buildMutationResult({ ok: false, error: 'CUSTOM_FIELD_ARCHIVE_FAILED', message: payload?.message || 'Impossibile archiviare il campo personalizzato.' });
  return buildMutationResult({ ok: true });
}
